import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import DateFilter from './DateFilter';
import KanbanBoard from './KanbanBoard';
import TaskDetailModel from './TaskDetailModel';
import DashboardHeader from './DashboardHeader';
const API = process.env.REACT_APP_BACKEND_API_URL;
const selectStyle = { padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', background: '#fff', minWidth: '200px' };
const labelStyle = { fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', display: 'block' };

// Item #3: Global Filters removed. Company Name + Recruiter Name kept (no
// Company SPOC filter), plus one combined Assigned/Reassigned Date filter.
const TeamLeadTasks = ({ tasks, currentUser, filterState, onFilterChange, filterCondition, onUpdateTaskStatus, onReassignTask, onLogout, fetchTasks }) => {
    const navigate = useNavigate();
    const [selectedTask, setSelectedTask] = useState(null);
    const [filterCompany, setFilterCompany] = useState('');
    const [filterRecruiter, setFilterRecruiter] = useState('');
    const [recruiterOptions, setRecruiterOptions] = useState([]);

    useEffect(() => {
        fetch(`${API}/api/users?role=recruiter`)
            .then(res => res.json())
            .then(data => setRecruiterOptions((data || []).map(u => u.name)))
            .catch(err => console.error('Failed to fetch recruiters:', err));
    }, []);

    const companyOptions = useMemo(() => Array.from(new Set(tasks.map(t => t.company).filter(Boolean))).sort(), [tasks]);
    const recruiterFilterOptions = useMemo(() => Array.from(new Set(tasks.map(t => t.assigned_to).filter(Boolean))).sort(), [tasks]);
    const hasFilters = filterCompany || filterRecruiter;

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={currentUser} titleText="Tasks" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <button
                    onClick={() => navigate('/team-lead')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px', background: '#fff',
                        border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '6px',
                        fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer'
                    }}
                >
                    <ArrowLeft size={14} /> Back to Dashboard
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Tasks</h2>
            </div>

            {/* Task creation now lives on the Created Tasks page — this view is
                the read-only board where the Team Lead tracks progress. */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '12px' }}>
                    <div>
                        <label style={labelStyle}>Company Name</label>
                        <select style={selectStyle} value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
                            <option value="">All Companies</option>
                            {companyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={labelStyle}>Recruiter Name</label>
                        <select style={selectStyle} value={filterRecruiter} onChange={(e) => setFilterRecruiter(e.target.value)}>
                            <option value="">All Recruiters</option>
                            {recruiterFilterOptions.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    {hasFilters && (
                        <button
                            type="button"
                            onClick={() => { setFilterCompany(''); setFilterRecruiter(''); }}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '9px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                        >
                            <X size={12} /> Clear
                        </button>
                    )}
                </div>
                <div style={{ maxWidth: '680px' }}>
                    {/* Item #3: one combined filter, matches Assigned Date OR Reassigned Date. */}
                    <label style={labelStyle}>Date (Assigned or Reassigned)</label>
                    <DateFilter dateField={['assigned_date', 'reassigned_date']} initialValue={filterState} onFilterChange={onFilterChange} />
                </div>
            </div>

            <KanbanBoard
                tasks={tasks}
                filterCondition={(t) => filterCondition(t) && (!filterCompany || t.company === filterCompany) && (!filterRecruiter || t.assigned_to === filterRecruiter)}
                onSelectTask={setSelectedTask}
                onUpdateTaskStatus={onUpdateTaskStatus}
                onReassignTask={onReassignTask}
                recruiterOptions={recruiterOptions}
                // Team Lead runs the full Recruiter workflow on tasks assigned to themselves
                // only; Recruiters' tasks stay view-only (backend enforces the same rule).
                canActOnTask={(t) => !!currentUser?.name && t.assigned_to === currentUser.name}
            />
            <TaskDetailModel task={selectedTask} onClose={() => setSelectedTask(null)} onUpdateTaskStatus={onUpdateTaskStatus} currentUser={currentUser} onTaskUpdated={fetchTasks} />
        </div>
    );
};

export default TeamLeadTasks;